import React, {useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { domain } from '../variables';
import Loader from './Loader';
import { demoBracket6, demoProA } from './Playoffs/playoffsDemo';
import { useTranslation } from '../i18n';

import {
    IoIosArrowUp
} from "react-icons/io";

// Tours des phases finales et journées (day_id) correspondantes, aller et retour.
// placement : match de classement joué les mêmes journées que le tour, affiché en dessous
// (places 5-6 : perdants des quarts, sous les demi-finales ; places 3-4 : perdants des demi-finales,
// sous la finale). previousDays : journées du tour précédent, qui permettent de les reconnaître.
// places : 1re place du match de classement, hors décalage des play-down (+6).
const ROUNDS = {
    proam: [
        {label: 'phase.semi', days: [23, 24]},
        {label: 'phase.final', days: [25]},
    ],
    default: [
        {label: 'phase.quarter', days: [16, 17]},
        {label: 'phase.semi', days: [18, 19], placement: {places: 5, previousDays: [16, 17]}},
        {label: 'phase.final', days: [20, 21], placement: {places: 3, previousDays: [18, 19]}},
    ],
};

// "jj/mm/aaaa" -> Date
const toDate = (value) => {
    const [day, month, year] = (value || '').split('/');
    return new Date(year, month - 1, day);
};

// "PARIS 13 TT 1" -> "PARIS 13 TT" : on retire seulement le numéro d'équipe final
const nomEquipe = (nom) => (nom || '').replace(/ +(?= )/g, '').trim().replace(/\s+1$/, '');

const isPlayed = (match) => match.home_points !== null && match.away_points !== null;

const isOnDays = (match, days) => days.includes(parseInt(match.day_id));

// Rencontre (aller + retour) entre deux équipes : clé indépendante de qui reçoit
const pairKey = (match) => [nomEquipe(match.domicile), nomEquipe(match.exterieur)].sort().join('|');

// Vainqueur d'une rencontre aller-retour : le plus de matchs gagnés, puis le plus de parties ;
// null tant qu'un match n'est pas joué (ou en cas d'égalité parfaite)
const pairWinner = (legs) => {
    if (legs.length === 0 || !legs.every(isPlayed)) return null;
    const wins = {};
    const parties = {};
    legs.forEach((match) => {
        const home = nomEquipe(match.domicile);
        const away = nomEquipe(match.exterieur);
        const [a, b] = [parseInt(match.home_points), parseInt(match.away_points)];
        wins[home] = (wins[home] || 0) + (a > b ? 1 : 0);
        wins[away] = (wins[away] || 0) + (b > a ? 1 : 0);
        parties[home] = (parties[home] || 0) + a;
        parties[away] = (parties[away] || 0) + b;
    });
    const [teamA, teamB] = Object.keys(wins);
    if (wins[teamA] !== wins[teamB]) return wins[teamA] > wins[teamB] ? teamA : teamB;
    if (parties[teamA] !== parties[teamB]) return parties[teamA] > parties[teamB] ? teamA : teamB;
    return null;
};

// Rencontres d'un tour : {main, placement}. Le match de classement oppose deux perdants du tour
// précédent (ex: places 3-4 = les deux perdants des demi-finales) ; une rencontre qu'on ne sait pas
// encore classer (tour précédent pas terminé) reste avec les rencontres principales.
const splitRound = (data, round) => {
    const list = data.filter((match) => isOnDays(match, round.days));
    if (!round.placement) return {main: list, placement: []};

    const previous = {};
    data.filter((match) => isOnDays(match, round.placement.previousDays)).forEach((match) => {
        (previous[pairKey(match)] = previous[pairKey(match)] || []).push(match);
    });
    const winners = new Set();
    const losers = new Set();
    Object.values(previous).forEach((legs) => {
        const winner = pairWinner(legs);
        if (!winner) return;
        winners.add(winner);
        [nomEquipe(legs[0].domicile), nomEquipe(legs[0].exterieur)].filter((team) => team !== winner).forEach((team) => losers.add(team));
    });
    const isPlacement = (match) => {
        const teams = [nomEquipe(match.domicile), nomEquipe(match.exterieur)];
        return teams.every((team) => losers.has(team) && !winners.has(team));
    };
    return {main: list.filter((match) => !isPlacement(match)), placement: list.filter(isPlacement)};
};

// Rencontres regroupées par date
const groupByDate = (list) => {
    const dates = [];
    [...list]
        .sort((a, b) => toDate(a.date) - toDate(b.date))
        .forEach((match) => {
            const last = dates[dates.length - 1];
            if (last && last.key === match.date) {
                last.matchs.push(match);
            }
            else {
                dates.push({key: match.date, date: toDate(match.date), matchs: [match]});
            }
        });
    return dates;
};

// Onglet Play-off / Play-down du module d'un championnat (accueil) : rencontres tour par tour,
// au même format que l'onglet Calendrier. offOrDown : "off" (play-off) ou "down" (play-down).
// demo : mode démo (?demo), rencontres fictives entre les équipes du classement (classement : réponse de getEquResult.php)
function BoxPlayoffs({championnat, offOrDown, demo = false, classement = null}) {
    const { t, locale } = useTranslation();
    const navigate = useNavigate();
    const rounds = ROUNDS[championnat] || ROUNDS.default;
    const [matchs, setMatchs] = useState(null);
    const [currentRound, setCurrentRound] = useState(0);

    useEffect(() => {
        setMatchs(null);
        if (demo) {
            if (classement) {
                const sorted = [...classement].sort((a, b) => a.clt - b.clt);
                const seed = (place) => sorted[place - 1] ? {title: sorted[place - 1].equipe, identifier: sorted[place - 1].numero} : null;
                let data;
                if (championnat === 'proam') {
                    data = demoProA(seed);
                }
                else if (offOrDown === 'off') {
                    data = demoBracket6(seed, 900000, championnat === 'proad');
                }
                else {
                    data = demoBracket6((place) => seed(place + 6), 900100);
                }
                setMatchs(data);
                setCurrentRound(getDefaultRound(data));
            }
            return;
        }
        axios.post(`${domain}/server/getPODMatchs.php`, {
            division: championnat,
            offOrDown,
            withSets: false,
        })
        .then((res) => {
            const data = Array.isArray(res.data) ? res.data : [];
            setMatchs(data);
            setCurrentRound(getDefaultRound(data));
        })
        .catch((error) => {
            console.log(error);
            setMatchs([]);
        });
    }, [championnat, offOrDown, demo, classement]);

    // Tour affiché par défaut : le premier qui a encore une rencontre à jouer, sinon le dernier programmé
    const getDefaultRound = (data) => {
        const programmed = rounds.map((round) => data.filter((match) => isOnDays(match, round.days)));
        const next = programmed.findIndex((list) => list.some((match) => !isPlayed(match)));
        if (next >= 0) return next;
        for (let index = programmed.length - 1; index >= 0; index--) {
            if (programmed[index].length > 0) return index;
        }
        return 0;
    };

    // Libellé d'un match de classement : "Places 3 - 4" ("Places 9 - 10" en play-down)
    const placementLabel = (placement) => {
        const first = placement.places + (offOrDown === 'down' ? 6 : 0);
        return t('playoffs.places', { a: first, b: first + 1 });
    };

    // Ouvre la rencontre dans le calendrier (comme le tableau des playoffs)
    const goToMatch = (match) => {
        navigate('/' + championnat + '/calendrier', { state: {
            off_down_matchs_day: match.day_id,
            off_down_matchs_id: match.renc_id,
        }});
    };

    const renderDates = (list) => groupByDate(list).map((item) => (
        <div key={item.key} className="box_calendar_date">
            <span className="box_calendar_date_span">{item.date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
            {
                item.matchs.map((match) => {
                    const played = isPlayed(match);
                    const scoreA = parseInt(match.home_points);
                    const scoreB = parseInt(match.away_points);
                    return (
                        <div
                            key={match.renc_id}
                            className={"box_calendar_match" + (played ? " clickable" : "")}
                            role={played ? "link" : undefined}
                            tabIndex={played ? 0 : undefined}
                            title={played ? t('box.seeMatch') : undefined}
                            onClick={played ? () => goToMatch(match) : undefined}
                            onKeyDown={played ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goToMatch(match); } } : undefined}
                        >
                            <span className={"box_calendar_equipe home" + (played && scoreA > scoreB ? " winner" : "")}>{nomEquipe(match.domicile)}</span>
                            {
                                played ?
                                <span className="box_calendar_time score">{scoreA} - {scoreB}</span>
                                :
                                <span className="box_calendar_time">{t('common.vs').toUpperCase()}</span>
                            }
                            <span className={"box_calendar_equipe away" + (played && scoreB > scoreA ? " winner" : "")}>{nomEquipe(match.exterieur)}</span>
                        </div>
                    );
                })
            }
        </div>
    ));

    const round = rounds[currentRound];
    const { main, placement } = matchs ? splitRound(matchs, round) : {main: [], placement: []};

    return (
        <div className="box_calendar">
            <div className="box_calendar_day">
                <button type="button" className="box_day_arrow" aria-label={t('box.previousRound')} disabled={currentRound <= 0} onClick={()=>setCurrentRound(currentRound - 1)}>
                    <IoIosArrowUp size={18} style={{transform: 'rotate(-90deg)'}} />
                </button>
                <span className="box_calendar_day_label">{t(round.label)}</span>
                <button type="button" className="box_day_arrow" aria-label={t('box.nextRound')} disabled={currentRound >= rounds.length - 1} onClick={()=>setCurrentRound(currentRound + 1)}>
                    <IoIosArrowUp size={18} style={{transform: 'rotate(90deg)'}} />
                </button>
            </div>
            <div className="box_calendar_matchs">
                {
                    matchs === null &&
                    <Loader label={t('loader.matches')} compact />
                }
                {
                    matchs !== null && main.length === 0 && placement.length === 0 &&
                    <span className="box_calendar_empty">{t('box.roundNotScheduled')}</span>
                }
                {matchs !== null && renderDates(main)}
                {
                    placement.length > 0 &&
                    <>
                        <span className="box_calendar_placement">{placementLabel(round.placement)}</span>
                        {renderDates(placement)}
                    </>
                }
            </div>
            <button type="button" className="box_span_details" onClick={()=>navigate('/' + championnat + '/playoffs' + (demo ? '?demo' : ''))}>
                {t('box.fullBracket')} <span aria-hidden="true">→</span>
            </button>
        </div>
    );
}

export default BoxPlayoffs;
